import { ChangeDetectionStrategy, Component, effect, signal, untracked, WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { LayerDataAttributesListComponent } from '@connect-services/client/components/field-intervention-layer-detail/layer-data-attributes-list/layer-data-attributes-list.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { AbstractLayerDetailComponent } from '@connect-services/client/components/project-settings/abstract-layer-detail.component';
import { ProjectSettingsLayerDetailTemplateComponent } from '@connect-services/client/components/project-settings/project-settings-layer-detail-template/project-settings-layer-detail-template.component';
import { Deposit, Layer } from '@connect-services/client/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LayerDataAttributesListComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        ProjectSettingsLayerDetailTemplateComponent,
        RessourcesModule,
    ],
    selector: 'app-interstandard-layer-detail',
    templateUrl: './interstandard-layer-detail.component.html',
})
export class InterstandardLayerDetailComponent extends AbstractLayerDetailComponent {
    protected readonly deposits: WritableSignal<Deposit[]> = signal([]);
    protected readonly Layer = Layer;
    protected readonly standardNameByDepositId: WritableSignal<{ [depositId: string]: string }> = signal({});

    public constructor() {
        super();

        effect(() => {
            if (!this.layerBloc.value() || this.layerBloc.value()?.type !== Layer.TypeEnum.INTERSTANDARD) {
                return;
            }

            untracked(async () => {
                await this.setDataAttributes();
                await this.setDeposits();
            });
        });
    }

    private async setDeposits(): Promise<void> {
        this.isLoading.set(true);

        const deposits: Deposit[] = await firstValueFrom(
            this.layersServiceSdk.layerControllerGetDepositsByInterstandardLayerId({
                id: this.layerBloc.value()!.id,
            }),
        );
        for (const deposit of deposits) {
            const depositWithStandard = await firstValueFrom(
                this.depositServiceSdk.depositControllerGetDepositWithFolderAndStandard({
                    id: deposit.id,
                }),
            );

            this.standardNameByDepositId.update((value) => ({
                ...value,
                [deposit.id]: depositWithStandard.standard?.name ?? '',
            }));
        }

        this.deposits.set(deposits);
        this.isLoading.set(false);
    }
}
